import React, { useState } from 'react';
import './aluminiumwindow.css';

import {
  FaLayerGroup,
  FaTools,
  FaShieldAlt,
  FaCompressArrowsAlt,
  FaRegBuilding,
  FaVolumeMute
} from 'react-icons/fa';

function Partition() {
  const [activeTab, setActiveTab] = useState('types');
  const [selectedImage, setSelectedImage] = useState(0);

  const images = [
    '/partition1.jpg',
    '/partition2.jpg',
    '/partition3.JPG',
    '/partition4.JPG',
    '/partition5.JPG',
  ];

  const features = [
    {
      icon: <FaLayerGroup />,
      title: 'Modular System',
      description: 'Flexible aluminium partition systems designed for easy reconfiguration and expansion'
    },
    {
      icon: <FaTools />,
      title: 'Quick Installation',
      description: 'Factory-finished components ensure fast, clean, and hassle-free installation'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Durable Construction',
      description: 'High-grade aluminium profiles provide long-lasting strength and stability'
    },
    {
      icon: <FaCompressArrowsAlt />,
      title: 'Space Optimization',
      description: 'Ideal solution to divide spaces efficiently without permanent construction'
    },
    {
      icon: <FaRegBuilding />,
      title: 'Modern Aesthetics',
      description: 'Sleek profiles and premium finishes for professional interiors'
    },
    {
      icon: <FaVolumeMute />,
      title: 'Acoustic Control',
      description: 'Glass and panel options designed to reduce noise and improve privacy'
    }
  ];

  const partitionTypes = [
    { name: 'Glass Partitions', image: '/partition3.JPG' },
    { name: 'Office Cubicles', image: '/Cubicle.avif' },
    { name: 'Panel Partitions', image: '/partition5.JPG' },
    { name: 'Sliding Partitions', image: '/sliding partition.jpg' },
  ];

  const specifications = [
    { label: 'Frame Material', value: '6063 Aluminium Alloy' },
    { label: 'Panel Options', value: 'Glass, ACP, MDF, Gypsum' },
    { label: 'Glass Types', value: 'Clear, Frosted, Tinted' },
    { label: 'Thickness', value: '8mm – 12mm Glass Options' },
    { label: 'Finish', value: 'Powder Coated / Anodized' },
    { label: 'Sound Insulation', value: 'Up to 40 dB' },
    { label: 'Customization', value: 'Fully Customizable Layouts' },
    { label: 'Warranty', value: '5–10 Years' }
  ];

  return (
    <div className="aluminium-window">

      {/* Hero Section */}
      <section className="aluminium-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Premium Aluminium Partitions</h1>
            <p className="hero-subtitle">
              Smart, flexible, and modern partition solutions designed to transform
              offices, commercial spaces, and interiors with style and efficiency.
            </p>
          </div>

          <div className="hero-image">
            <img
              src={images[selectedImage]}
              alt="Aluminium Partition System"
              className="hero-main-image"
            />
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Partition ${index + 1}`}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="section-header">
          <h2>Why Choose Our Aluminium Partitions?</h2>
          <p>Designed for modern interiors and professional environments</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs */}
      <section className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'types' ? 'active' : ''}`}
            onClick={() => setActiveTab('types')}
          >
            Partition Types
          </button>

          <button
            className={`tab-btn ${activeTab === 'benefits' ? 'active' : ''}`}
            onClick={() => setActiveTab('benefits')}
          >
            Benefits
          </button>
        </div>

        <div className="tabs-content">

          {activeTab === 'types' && (
            <div className="window-types-grid">
              {partitionTypes.map((type, index) => (
                <div key={index} className="window-type-card">
                  <img src={type.image} alt={type.name} />
                  <h4>{type.name}</h4>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'benefits' && (
            <div className="benefits-content">
              <div className="benefit-card">
                <h3>Flexible Layouts</h3>
                <p>Easily reconfigure office and commercial spaces without major renovations.</p>
              </div>
              <div className="benefit-card">
                <h3>Professional Appearance</h3>
                <p>Creates a clean, modern, and organized working environment.</p>
              </div>
              <div className="benefit-card">
                <h3>Cost Effective</h3>
                <p>More economical than permanent walls while offering premium finishes.</p>
              </div>
              <div className="benefit-card">
                <h3>Natural Light</h3>
                <p>Glass partitions allow light flow, improving productivity and comfort.</p>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Upgrade Your Interior Space</h2>
          <p>Contact us today for customized aluminium partition solutions</p>
          <div className="cta-buttons">
            <a href="/contactus">
              <button className="btn-outline">Contact Us</button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Are aluminium partitions removable?</h4>
            <p>Yes, they are modular and can be relocated or reconfigured easily.</p>
          </div>
          <div className="faq-item">
            <h4>Do partitions provide sound insulation?</h4>
            <p>Glass and panel options offer effective sound reduction for office privacy.</p>
          </div>
          <div className="faq-item">
            <h4>Can designs be customized?</h4>
            <p>Absolutely. Layouts, finishes, glass types, and panel materials are fully customizable.</p>
          </div>
          <div className="faq-item">
            <h4>Where can aluminium partitions be used?</h4>
            <p>Ideal for offices, hospitals, showrooms, banks, schools, and commercial spaces.</p>
          </div>
        </div>
      </section>

    </div>
  );
}




export default Partition